/**
 * UsernameManager
 *
 * Manages player usernames for leaderboard tracking
 * - Auto-generates fun usernames (AdjectiveAnimal format)
 * - Stores in localStorage
 * - Validates username format
 */

export class UsernameManager {
  private static readonly STORAGE_KEY = 'blobo-username';
  private static readonly MIN_LENGTH = 2;
  private static readonly MAX_LENGTH = 20;

  // Fun adjectives for username generation (expanded for more combinations)
  private static readonly ADJECTIVES = [
    'Swift',
    'Speedy',
    'Quick',
    'Rapid',
    'Blazing',
    'Nimble',
    'Agile',
    'Graceful',
    'Daring',
    'Bold',
    'Brave',
    'Fearless',
    'Mighty',
    'Epic',
    'Legendary',
    'Clever',
    'Sneaky',
    'Tricky',
    'Cunning',
    'Slick',
    'Wild',
    'Fierce',
    'Savage',
    'Feral',
    'Primal',
    'Bouncy',
    'Jumpy',
    'Leaping',
    'Soaring',
    'Flying',
    'Lucky',
    'Happy',
    'Jolly',
    'Cheerful',
    'Merry',
    'Cosmic',
    'Stellar',
    'Astral',
    'Mystic',
    'Magic',
    'Thunder',
    'Lightning',
    'Storm',
    'Frost',
    'Blaze',
    'Shadow',
    'Stealth',
    'Silent',
    'Phantom',
    'Ghost',
    'Turbo',
    'Hyper',
    'Ultra',
    'Super',
    'Mega',
    'Noble',
    'Royal',
    'Regal',
    'Grand',
    'Majestic',
    'Wise',
    'Ancient',
    'Eternal',
    'Divine',
    'Celestial',
    'Fiery',
    'Icy',
    'Stormy',
    'Windy',
    'Cloudy',
    'Golden',
    'Silver',
    'Crystal',
    'Diamond',
    'Emerald',
    'Crimson',
    'Azure',
    'Violet',
    'Scarlet',
    'Amber',
    'Radiant',
    'Shining',
    'Glowing',
    'Bright',
    'Vivid',
    'Dashing',
    'Vibrant',
    'Dynamic',
    'Energetic',
    'Lively',
    'Fluffy',
    'Fuzzy',
    'Cuddly',
    'Tiny',
    'Giant',
    'Mighty',
    'Powerful',
    'Strong',
    'Fearsome',
    'Valiant',
  ];

  // Fun animals for username generation (expanded for more combinations)
  private static readonly ANIMALS = [
    'Badger',
    'Squirrel',
    'Fox',
    'Wolf',
    'Bear',
    'Eagle',
    'Hawk',
    'Falcon',
    'Raven',
    'Owl',
    'Tiger',
    'Lion',
    'Panther',
    'Leopard',
    'Cheetah',
    'Rabbit',
    'Hare',
    'Deer',
    'Moose',
    'Elk',
    'Dragon',
    'Phoenix',
    'Griffin',
    'Pegasus',
    'Unicorn',
    'Otter',
    'Beaver',
    'Raccoon',
    'Panda',
    'Koala',
    'Gecko',
    'Lizard',
    'Frog',
    'Toad',
    'Newt',
    'Shark',
    'Dolphin',
    'Whale',
    'Seal',
    'Walrus',
    'Monkey',
    'Gorilla',
    'Lemur',
    'Sloth',
    'Puma',
    'Wombat',
    'Platypus',
    'Dingo',
    'Lynx',
    'Bobcat',
    'Jaguar',
    'Cougar',
    'Ocelot',
    'Caracal',
    'Serval',
    'Raven',
    'Crow',
    'Jay',
    'Magpie',
    'Starling',
    'Turtle',
    'Tortoise',
    'Salamander',
    'Axolotl',
    'Iguana',
    'Mantis',
    'Beetle',
    'Wasp',
    'Hornet',
    'Spider',
    'Viper',
    'Cobra',
    'Python',
    'Anaconda',
    'Mamba',
    'Mustang',
    'Stallion',
    'Bronco',
    'Colt',
    'Mare',
    'Bison',
    'Buffalo',
    'Yak',
    'Ox',
    'Bull',
    'Condor',
    'Vulture',
    'Osprey',
    'Kite',
    'Harrier',
  ];

  /**
   * Get current username (generate if not exists)
   */
  static getUsername(): string {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved && this.validateUsername(saved)) {
      return saved;
    }

    // Generate new username
    const username = this.generateUsername();
    this.setUsername(username);
    return username;
  }

  /**
   * Generate random username: AdjectiveAnimal format
   * Examples: "SpeedySquirrel", "BraveBadger", "MysticFox"
   */
  static generateUsername(): string {
    const adjective = this.ADJECTIVES[Math.floor(Math.random() * this.ADJECTIVES.length)];
    const animal = this.ANIMALS[Math.floor(Math.random() * this.ANIMALS.length)];
    return `${adjective}${animal}`;
  }

  /**
   * Update username (with validation)
   */
  static setUsername(username: string): boolean {
    if (!this.validateUsername(username)) {
      console.error('Invalid username:', username);
      return false;
    }

    const sanitized = username.trim().substring(0, this.MAX_LENGTH);
    localStorage.setItem(this.STORAGE_KEY, sanitized);
    return true;
  }

  /**
   * Validate username format
   * - 2-20 characters
   * - Alphanumeric, underscore, dash only
   */
  static validateUsername(username: string): boolean {
    if (!username || typeof username !== 'string') return false;

    const trimmed = username.trim();
    if (trimmed.length < this.MIN_LENGTH) return false;
    if (trimmed.length > this.MAX_LENGTH) return false;

    // Allow alphanumeric, underscore, dash
    const validFormat = /^[a-zA-Z0-9_-]+$/.test(trimmed);
    return validFormat;
  }

  /**
   * Reset username (generates new one)
   */
  static resetUsername(): string {
    const newUsername = this.generateUsername();
    this.setUsername(newUsername);
    return newUsername;
  }

  /**
   * Check if username exists
   */
  static hasUsername(): boolean {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    return saved !== null && this.validateUsername(saved);
  }

  /**
   * Check if username is available (not taken by another player)
   */
  static async checkAvailability(
    username: string,
    currentUsername?: string
  ): Promise<{ available: boolean; message: string }> {
    try {
      // If checking against current username, it's always available
      if (currentUsername && username.toLowerCase() === currentUsername.toLowerCase()) {
        return { available: true, message: 'This is your current username' };
      }

      const response = await fetch(
        `/api/username/check-availability?username=${encodeURIComponent(username)}`
      );
      const data = await response.json();

      return {
        available: data.available || false,
        message:
          data.message || (data.available ? 'Username is available' : 'Username is already taken'),
      };
    } catch (error) {
      console.error('Failed to check username availability:', error);
      return {
        available: false,
        message: 'Failed to check availability. Please try again.',
      };
    }
  }

  /**
   * Generate a unique available username (with retry logic)
   * Tries up to 10 times to find an available name
   */
  static async generateAvailableUsername(): Promise<string> {
    const maxAttempts = 10;

    for (let i = 0; i < maxAttempts; i++) {
      const username = this.generateUsername();
      const { available } = await this.checkAvailability(username);

      if (available) {
        return username;
      }

      console.log(`Username ${username} is taken, trying another...`);
    }

    // If all attempts fail, return a username anyway (very unlikely with 9,000 combinations)
    console.warn('Failed to find available username after 10 attempts, using generated name');
    return this.generateUsername();
  }
}
